/**
 * 問い合わせ管理ページ
 */

import type { ContactCategory, ContactRecord, ContactStatus } from "@repo/types";
import { CONTACT_CATEGORY_LABELS, CONTACT_STATUS_LABELS } from "@repo/types";
import {
  AlertCircle,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Filter,
  Loader2,
  Mail,
  MessageSquare,
  StickyNote,
  User,
} from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { CategoryBadge } from "@/features/contacts/components/category-badge";
import { StatusBadge } from "@/features/contacts/components/status-badge";
import { useContact, useContactStats, useContacts } from "@/features/contacts/hooks/use-contacts";
import { AddNoteDialog } from "./dialogs/add-note-dialog";
import { ReplyDialog } from "./dialogs/reply-dialog";
import { StatusUpdateDialog } from "./dialogs/status-update-dialog";

// ============================================
// 統計カード
// ============================================

function StatsCards() {
  const { stats, isLoading } = useContactStats();

  if (isLoading || !stats) {
    return null;
  }

  return (
    <div className="grid gap-4 md:grid-cols-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">総件数</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.total}</div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">未対応</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-yellow-600">{stats.byStatus.pending}</div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">確認済み</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-blue-600">{stats.byStatus.reviewed}</div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">解決済み</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-green-600">{stats.byStatus.resolved}</div>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================
// フィルター
// ============================================

function ContactFilters({
  status,
  category,
  startDate,
  endDate,
  onStatusChange,
  onCategoryChange,
  onStartDateChange,
  onEndDateChange,
}: {
  status?: ContactStatus;
  category?: ContactCategory;
  startDate?: string;
  endDate?: string;
  onStatusChange: (value: ContactStatus | undefined) => void;
  onCategoryChange: (value: ContactCategory | undefined) => void;
  onStartDateChange: (value: string | undefined) => void;
  onEndDateChange: (value: string | undefined) => void;
}) {
  return (
    <div className="flex flex-wrap items-end gap-4">
      <div className="space-y-1">
        <Label className="text-xs">ステータス</Label>
        <Select
          value={status || "all"}
          onValueChange={(v) => onStatusChange(v === "all" ? undefined : (v as ContactStatus))}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="すべて" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">すべて</SelectItem>
            {Object.entries(CONTACT_STATUS_LABELS).map(([key, label]) => (
              <SelectItem key={key} value={key}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1">
        <Label className="text-xs">カテゴリ</Label>
        <Select
          value={category || "all"}
          onValueChange={(v) => onCategoryChange(v === "all" ? undefined : (v as ContactCategory))}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="すべて" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">すべて</SelectItem>
            {Object.entries(CONTACT_CATEGORY_LABELS).map(([key, label]) => (
              <SelectItem key={key} value={key}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1">
        <Label className="text-xs">開始日</Label>
        <Input
          type="date"
          value={startDate || ""}
          onChange={(e) => onStartDateChange(e.target.value || undefined)}
          className="w-[150px]"
        />
      </div>

      <div className="space-y-1">
        <Label className="text-xs">終了日</Label>
        <Input
          type="date"
          value={endDate || ""}
          onChange={(e) => onEndDateChange(e.target.value || undefined)}
          className="w-[150px]"
        />
      </div>
    </div>
  );
}

// ============================================
// 問い合わせ一覧
// ============================================

function ContactList({
  contacts,
  selectedId,
  onSelect,
}: {
  contacts: ContactRecord[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  if (contacts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <MessageSquare className="mb-4 h-12 w-12" />
        <p>問い合わせがありません</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {contacts.map((contact) => {
        const isSelected = contact.id === selectedId;
        return (
          <button
            key={contact.id}
            type="button"
            onClick={() => onSelect(contact.id)}
            className={`w-full text-left rounded-lg border p-3 transition-colors hover:bg-accent ${
              isSelected ? "border-primary bg-accent" : "border-border"
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <StatusBadge status={contact.status} size="sm" />
                <CategoryBadge category={contact.category} size="sm" />
              </div>
              <span className="text-xs text-muted-foreground">
                {new Date(contact.createdAt).toLocaleDateString("ja-JP")}
              </span>
            </div>
            <h4 className="mt-2 font-medium line-clamp-1">{contact.subject}</h4>
            <p className="mt-1 text-sm text-muted-foreground line-clamp-1">{contact.message}</p>
            <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
              <User className="h-3 w-3" />
              <span>{contact.name || contact.email}</span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ============================================
// ページネーション
// ============================================

function Pagination({
  page,
  totalPages,
  onPageChange,
}: {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}) {
  return (
    <div className="flex items-center justify-between border-t pt-4">
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(page - 1)}
        disabled={page <= 1}
      >
        <ChevronLeft className="mr-1 h-4 w-4" />
        前へ
      </Button>
      <span className="text-sm text-muted-foreground">
        {page} / {totalPages || 1} ページ
      </span>
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(page + 1)}
        disabled={page >= totalPages}
      >
        次へ
        <ChevronRight className="ml-1 h-4 w-4" />
      </Button>
    </div>
  );
}

// ============================================
// 問い合わせ詳細
// ============================================

function ContactDetail({
  contact,
  onStatusUpdate,
  onAddNote,
  onSendReply,
  isUpdating,
}: {
  contact: ContactRecord;
  onStatusUpdate: (status: ContactStatus, note?: string) => Promise<boolean>;
  onAddNote: (content: string) => Promise<boolean>;
  onSendReply: (subject: string, message: string) => Promise<boolean>;
  isUpdating: boolean;
}) {
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);

  const handleStatusUpdate = async (status: ContactStatus, note?: string) => {
    const success = await onStatusUpdate(status, note);
    if (success) {
      toast.success("ステータスを更新しました");
      setStatusDialogOpen(false);
    } else {
      toast.error("ステータスの更新に失敗しました");
    }
    return success;
  };

  const handleAddNote = async (content: string) => {
    const success = await onAddNote(content);
    if (success) {
      toast.success("メモを追加しました");
      setNoteDialogOpen(false);
    } else {
      toast.error("メモの追加に失敗しました");
    }
    return success;
  };

  const handleSendReply = async (subject: string, message: string) => {
    const success = await onSendReply(subject, message);
    if (success) {
      toast.success("返信を送信しました");
      setReplyDialogOpen(false);
    } else {
      toast.error("返信の送信に失敗しました");
    }
    return success;
  };

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <StatusBadge status={contact.status} />
            <CategoryBadge category={contact.category} />
          </div>
          <h2 className="mt-2 text-xl font-semibold">{contact.subject}</h2>
          <div className="mt-1 flex items-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <User className="h-4 w-4" />
              {contact.name || "匿名"}
            </span>
            <span className="flex items-center gap-1">
              <Mail className="h-4 w-4" />
              {contact.email}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              {new Date(contact.createdAt).toLocaleString("ja-JP")}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setStatusDialogOpen(true)}>
            ステータス変更
          </Button>
          <Button variant="outline" size="sm" onClick={() => setNoteDialogOpen(true)}>
            <StickyNote className="mr-1 h-4 w-4" />
            メモ追加
          </Button>
          <Button size="sm" onClick={() => setReplyDialogOpen(true)}>
            <Mail className="mr-1 h-4 w-4" />
            返信
          </Button>
        </div>
      </div>

      <Separator />

      {/* 本文 */}
      <div>
        <h3 className="mb-2 font-medium">お問い合わせ内容</h3>
        <div className="rounded-lg bg-muted p-4 whitespace-pre-wrap">{contact.message}</div>
      </div>

      {/* 対応履歴 */}
      {contact.actions.length > 0 && (
        <div>
          <h3 className="mb-3 font-medium">対応履歴</h3>
          <div className="space-y-3">
            {contact.actions.map((action) => (
              <div key={action.id} className="rounded-lg border p-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">
                    {action.type === "status_changed" && "ステータス変更"}
                    {action.type === "note_added" && "メモ追加"}
                    {action.type === "reply_sent" && "返信送信"}
                  </span>
                  <span className="text-muted-foreground">
                    {new Date(action.performedAt).toLocaleString("ja-JP")}
                  </span>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">by {action.performedBy}</p>
                {action.details.type === "status_changed" && (
                  <div className="mt-2 flex items-center gap-2 text-sm">
                    <StatusBadge status={action.details.from} size="sm" />
                    <span>→</span>
                    <StatusBadge status={action.details.to} size="sm" />
                    {action.details.note && (
                      <span className="ml-2 text-muted-foreground">{action.details.note}</span>
                    )}
                  </div>
                )}
                {action.details.type === "note_added" && (
                  <div className="mt-2 rounded bg-muted p-2 text-sm">{action.details.content}</div>
                )}
                {action.details.type === "reply_sent" && (
                  <div className="mt-2 space-y-1 text-sm">
                    <p className="font-medium">件名: {action.details.subject}</p>
                    <div className="rounded bg-muted p-2 whitespace-pre-wrap">
                      {action.details.message}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ダイアログ */}
      <StatusUpdateDialog
        open={statusDialogOpen}
        onOpenChange={setStatusDialogOpen}
        currentStatus={contact.status}
        onSubmit={handleStatusUpdate}
        isSubmitting={isUpdating}
      />
      <AddNoteDialog
        open={noteDialogOpen}
        onOpenChange={setNoteDialogOpen}
        onSubmit={handleAddNote}
        isSubmitting={isUpdating}
      />
      <ReplyDialog
        open={replyDialogOpen}
        onOpenChange={setReplyDialogOpen}
        contactEmail={contact.email}
        contactSubject={contact.subject}
        onSubmit={handleSendReply}
        isSubmitting={isUpdating}
      />
    </div>
  );
}

// ============================================
// メインページ
// ============================================

export function ContactsPage() {
  const navigate = useNavigate();
  const { contactId } = useParams<{ contactId?: string }>();
  const selectedId = contactId || null;

  const { contacts, pagination, isLoading, error, query, setQuery, refresh } = useContacts();
  const {
    contact,
    isLoading: isLoadingContact,
    updateStatus,
    addNote,
    sendReply,
  } = useContact(selectedId);
  const [isUpdating, setIsUpdating] = useState(false);

  // 問い合わせ選択
  const handleSelect = (id: string) => {
    navigate(`/contacts/${id}`);
  };

  // ステータス更新
  const handleStatusUpdate = async (status: ContactStatus, note?: string) => {
    setIsUpdating(true);
    try {
      const success = await updateStatus(status, note);
      if (success) {
        await refresh();
      }
      return success;
    } finally {
      setIsUpdating(false);
    }
  };

  // メモ追加
  const handleAddNote = async (content: string) => {
    setIsUpdating(true);
    try {
      return await addNote(content);
    } finally {
      setIsUpdating(false);
    }
  };

  // 返信送信
  const handleSendReply = async (subject: string, message: string) => {
    setIsUpdating(true);
    try {
      const success = await sendReply(subject, message);
      if (success) {
        await refresh();
      }
      return success;
    } finally {
      setIsUpdating(false);
    }
  };

  // ローディング
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // エラー
  if (error) {
    return (
      <Card>
        <CardHeader className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-destructive" />
          <CardTitle className="mt-4">エラー</CardTitle>
          <CardDescription>{error}</CardDescription>
        </CardHeader>
        <CardContent className="flex justify-center">
          <Button onClick={refresh}>再読み込み</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* 統計カード */}
      <StatsCards />

      {/* メインコンテンツ */}
      <div className="grid gap-6 lg:grid-cols-[400px_1fr]">
        {/* サイドバー */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                問い合わせ一覧
              </CardTitle>
              <CardDescription>
                {pagination ? `${pagination.total}件の問い合わせ` : "読み込み中..."}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* フィルター */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Filter className="h-4 w-4" />
                  フィルター
                </div>
                <ContactFilters
                  status={query.status}
                  category={query.category}
                  startDate={query.startDate}
                  endDate={query.endDate}
                  onStatusChange={(v) => setQuery({ status: v })}
                  onCategoryChange={(v) => setQuery({ category: v })}
                  onStartDateChange={(v) => setQuery({ startDate: v })}
                  onEndDateChange={(v) => setQuery({ endDate: v })}
                />
              </div>

              <Separator />

              {/* 一覧 */}
              <ContactList contacts={contacts} selectedId={selectedId} onSelect={handleSelect} />

              {/* ページネーション */}
              {pagination && (
                <Pagination
                  page={pagination.page}
                  totalPages={pagination.totalPages}
                  onPageChange={(p) => setQuery({ page: p })}
                />
              )}
            </CardContent>
          </Card>
        </div>

        {/* 詳細 */}
        <div>
          <Card>
            <CardContent className="pt-6">
              {selectedId && contact ? (
                <ContactDetail
                  contact={contact}
                  onStatusUpdate={handleStatusUpdate}
                  onAddNote={handleAddNote}
                  onSendReply={handleSendReply}
                  isUpdating={isUpdating}
                />
              ) : isLoadingContact ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center text-muted-foreground">
                  <MessageSquare className="mb-4 h-12 w-12" />
                  <p>左のリストから問い合わせを選択してください</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
